<script setup>
   import { onMounted, ref, watchEffect, inject, computed } from 'vue'
   import { useRouter, useRoute } from 'vue-router'
   import { limitCharacters } from '@/composables/strings'
   import { modalHide, modalShow, modalShowing } from '@/composables/bootstrap'
   import FileCard from '@/components/Cards/FileCard.vue'

   const router = useRouter()
   const route = useRoute()

   const $storeSearch = inject('$storeSearch')
   const $storeApp = inject('$storeApp')
   const $storeUser = inject('$storeUser')
   const $storeAuth = inject('$storeAuth')
   const $storeGinnels = inject('$storeGinnels')
   const $storeGinnel = inject('$storeGinnel')

   const props = defineProps({
      action: {
         type: String,
         required: true
      },
      searchOpened: {
         type: String,
         required: true
      }
   })

  /**
    * getModalStatus
    * When the modal opens
  */
   const searchInput = ref('')
	const currentModal = ref({})
   const getModalStatus = () => {
    if(currentModal.value?.event == 'opened' && currentModal.value?.id == 'search') {
      searchInput.value.select()
    }
  }

	watchEffect(() => {
		currentModal.value = $storeApp.modal
      getModalStatus()
	})

   /**
      * Function: getGinnels
      * @description Get the ginnels when navigating
   */
   const getGinnels = collection_id => {
      $storeGinnels.get({ collection_id: collection_id })
      modalHide('search')
   }

   const viewingFile = ref(null)
   const gotoFile = file_id => {
      $storeSearch.toFileFromSearch(file_id)
      viewingFile.value = file_id
   }

   /**
      * Function: search
      * @description The user has submitted their query for the search
   */
   const searchQuery = ref('')
   const searched = ref(false)
   const pauseSearch = ref(false)
   const search = () => {
      if(searchQuery.value.length && ! pauseSearch.value) {
         $storeSearch.search(searchQuery.value)
         searched.value = true
         pauseSearch.value = true

         setTimeout(() => {
            pauseSearch.value = false
         }, 1000)
      }
   }

   const hasSearchedNoResults = () => {
      if(searchQuery.value.length > 0 && searched.value && ! searchResults.value?.ginnels?.length && ! searchResults.value?.collections?.length && ! searchResults.value?.files?.length)
         return true
      else if(! searchQuery.value.length && searched.value && ! searchResults.value?.ginnels?.length && ! searchResults.value?.collections?.length && ! searchResults.value?.files?.length)
         searched.value = false
      else
         return false
   }

   const notSearched = computed(() => {
      if(! searchQuery.value.length && ! searched.value)
         return true
      else
         return false
   })

   const searchResults = ref([])
   watchEffect(() => {
      searchResults.value = $storeSearch.results
   })
</script>

<template>

   <!-- Delete Ginnel Modal -->
   <Modal v-if="action == 'search'" id="search" size="x-large" instant>
      <template #body>
         <div class="main-search">
            <form @submit.prevent="search()" class="input-group" :class="{ saving: pauseSearch }">
               <input v-model="searchQuery" ref="searchInput" type="text" class="form-control" placeholder="Search">

               <button class="btn btn-outline-secondary d-flex align-items-center" type="submit">
                  <FontAwesomeIcon icon="fa fa-search" />
               </button>
            </form>
         </div>

         <div v-if="pauseSearch"class="mt-3">
            <LoadingBar :duration="1" />
         </div>

            <div v-if="! pauseSearch" class="main-search-results" ref="searchResultsTarget">

            <!-- Ginnels Files Results -->
               <div v-if="searchResults?.files?.length">
                  <h3 class="p-2 m-0">
                     Ginnel Files
                  </h3>

                  <div class="cg-dark-container">
                     <div class="row g-2">

                        <div v-for="file in searchResults.files" :key="file?.file?.file_id" class="col-4 col-xl-2">
                           <RouterLink @click="getGinnels(file?.collection?.collection_id); gotoFile(file?.file?.file_id)" :to="{ name: 'collection.ginnel', params: { collection_id: file?.collection?.collection_id, ginnel_id: file?.ginnel?.ginnel_id }, query: { file: file?.fild_id } }" class="results-link">
                              <FileCard :file="file?.file" :ginnel="file?.ginnel" :collection="file?.collection" :selected="viewingFile == file?.file?.file_id" compact />
                           </RouterLink>
                        </div>
                     </div>
                  </div>
               </div>

            <!-- Ginnels Results -->
               <div v-if="searchResults?.ginnels?.length">
                  <h3 class="p-2 m-0">
                     Ginnels
                  </h3>

                  <nav>
                     <template v-for="ginnel in searchResults.ginnels" :key="ginnel.id">
                        <RouterLink @click="getGinnels(ginnel?.collection?.collection_id)" :to="{ name: 'collection.ginnel', params: { collection_id: ginnel?.collection?.collection_id, ginnel_id: ginnel.ginnel_id } }" class="results-link">
                           <div class="d-flex align-items-center justify-content-between gap-2">
                              <div>
                                 <FontAwesomeIcon v-if="ginnel.ginnel_id == $route.params.ginnel_id" icon="fas fa-folder-open" />
                                 <FontAwesomeIcon v-else icon="fas fa-folder" />
                                 {{ ginnel.ginnel_name }}
                              </div>
                              <span class="badge bg-secondary">{{ ginnel?.files_count }} File{{ ginnel?.files_count != 1 ? 's' : '' }}</span>
                           </div>

                           <span v-if="ginnel?.description" class="ginnel-preview text-muted">{{ limitCharacters(ginnel?.description, 120) }}</span>
                           <span v-else class="ginnel-preview text-muted">No Description.</span>
                        </RouterLink>
                     </template>
                  </nav>
               </div>

               <!-- Collections Results -->
               <div v-if="searchResults?.collections?.length">
                  <h3 class="p-2 m-0">
                     Collections
                  </h3>

                  <nav>
                     <template v-for="collection in searchResults.collections" :key="collection.id">
                        <RouterLink
                           :to="{ name: 'collection', params: { collection_id: collection?.collection_id } }"
                           @click="getGinnels(collection?.collection_id)"
                           class="results-link">

                           <div class="d-flex align-items-center justify-content-between gap-2">
                              <div><FontAwesomeIcon icon="fas fa-layer-group" /> {{ collection?.collection_name }}</div>
                              <span class="badge bg-secondary">{{ collection?.ginnels_count }} Ginnel{{ collection?.ginnels_count != 1 ? 's' : '' }}</span>
                           </div>

                        </RouterLink>
                     </template>
                  </nav>
               </div>

               <div v-if="hasSearchedNoResults()">
                  <h3 class="m-0">
                     No results found.
                  </h3>
               </div>

               <div v-if="notSearched">
                  <h3 class="m-0 text-center">
                     Search Collections, Ginnels, Files, and Tags.
                  </h3>
               </div>
            </div>
      </template>
   </Modal>
</template>